import React, { useState } from 'react';
import { useMutation, gql } from '@apollo/client';
import { DateTime } from 'luxon';
import { useToast } from '../../components/Toast/ToastContext';
import { useRescueContext } from '../../lib/RescueContext';
import { GWA_PROGRAM_QUERY } from './Gwa';
import type { GwaRedemptionItem } from './Gwa';
import styles from './GwaDashboard.module.scss';

const UPDATE_PROMO_CODE_MUTATION = gql`
  mutation RESCUE_updateGwaPromoCode($input: UpdateGwaPromoCodeInput!) {
    rescueUpdateGwaPromoCode(input: $input) {
      enrollment {
        id
        slug
        promoCodeAlias
        pdpUrl
        active
        totalRedemptions
        monthRedemptions
      }
    }
  }
`;

interface Enrollment {
  id: string;
  slug: string;
  promoCodeAlias: string;
  pdpUrl: string;
  active: boolean;
  createdAt: string;
  totalRedemptions: number;
  monthRedemptions: number;
  recentRedemptions: GwaRedemptionItem[];
}

interface GwaDashboardProps {
  enrollment: Enrollment;
}

function buildBlurb(template: string, pdpUrl: string, promoCode: string, orgName: string) {
  return template
    .replace(/\{\{pdpUrl\}\}/g, pdpUrl)
    .replace(/\{\{promoCode\}\}/g, promoCode)
    .replace(/\{\{orgName\}\}/g, orgName);
}

const EMAIL_TEMPLATE = `Congrats on your new family member! {{orgName}} has partnered with Fi to give you a free Fi Mini GPS Tracker — the smallest, lightest dog GPS ever made.

The Fi Mini weighs just half an ounce, lasts up to 6 weeks on a single charge, and tracks your dog's location in real time. Plus you'll get daily activity and sleep insights to learn your new dog's routine.

Visit {{pdpUrl}} and use code {{promoCode}} at checkout to claim yours — 6 months free, then $189/yr.`;

const PRINT_TEMPLATE = `FREE FI MINI GPS TRACKER FOR YOUR NEW DOG

{{orgName}} has partnered with Fi to give you a free Fi Mini — the smallest, lightest dog GPS ever made.

• Real-time GPS location tracking
• Daily activity & sleep insights
• Half an ounce with 6-week battery life

Claim yours at: {{pdpUrl}}
Use code: {{promoCode}}
6 months free, then $189/yr.`;

type BlurbTab = 'email' | 'print';

export default function GwaDashboard({ enrollment }: GwaDashboardProps) {
  const { showToast } = useToast();
  const { orgName } = useRescueContext();
  const [activeBlurbTab, setActiveBlurbTab] = useState<BlurbTab>('email');
  const [editing, setEditing] = useState(false);
  const [newCode, setNewCode] = useState(enrollment.promoCodeAlias);
  const [editError, setEditError] = useState('');
  const [updatePromoCode, { loading: updating }] = useMutation(UPDATE_PROMO_CODE_MUTATION, {
    refetchQueries: [{ query: GWA_PROGRAM_QUERY }],
  });

  const emailBlurb = buildBlurb(EMAIL_TEMPLATE, enrollment.pdpUrl, enrollment.promoCodeAlias, orgName);
  const printBlurb = buildBlurb(PRINT_TEMPLATE, enrollment.pdpUrl, enrollment.promoCodeAlias, orgName);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(
      () => showToast(`${label} copied!`),
      () => showToast('Failed to copy'),
    );
  };

  const handleSaveCode = async () => {
    setEditError('');
    const trimmed = newCode.trim().toUpperCase();
    if (trimmed.length < 3 || trimmed.length > 20) {
      setEditError('Code must be 3-20 characters');
      return;
    }
    if (!/^[A-Z0-9-]+$/.test(trimmed)) {
      setEditError('Only letters, numbers, and hyphens allowed');
      return;
    }
    try {
      await updatePromoCode({ variables: { input: { promoCodeAlias: trimmed } } });
      setEditing(false);
      showToast('Promo code updated — the old code no longer works');
    } catch (err: any) {
      const msg = err?.graphQLErrors?.[0]?.message || err?.message || 'Something went wrong';
      setEditError(msg);
    }
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setNewCode(enrollment.promoCodeAlias);
    setEditError('');
  };

  return (
    <div>
      <h1>Gift with Adoption</h1>

      <div className={styles.successBanner}>
        <svg viewBox="0 0 24 24" className={styles.checkIcon}>
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <span>You're enrolled. Your link and promo code are live.</span>
      </div>

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Redemptions</div>
          <div className={styles.statValue}>{enrollment.monthRedemptions}</div>
          <div className={styles.statSubtitle}>last 30 days</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Total Redemptions</div>
          <div className={styles.statValue}>{enrollment.totalRedemptions}</div>
          <div className={styles.statSubtitle}>all time</div>
        </div>
      </div>

      <div className={styles.offerCard}>
        <div className={styles.offerTitle}>Your rescue's unique offer</div>
        <p className={styles.offerDescription}>
          Adopters who visit your link and apply your code get a Fi Mini GPS Tracker for <strong>6 months free</strong>{' '}
          (renews at $189/yr). Share with recent and previous adopters — not for public or social media use.
        </p>
        <div className={styles.offerFields}>
          <div className={styles.offerField}>
            <div className={styles.offerFieldLabel}>Link</div>
            <div className={styles.offerFieldValue}>
              <span>{enrollment.pdpUrl}</span>
              <button
                type="button"
                className={styles.copyBtn}
                onClick={() => copyToClipboard(enrollment.pdpUrl, 'Link')}
              >
                Copy
              </button>
            </div>
          </div>
          <div className={styles.offerField}>
            <div className={styles.offerFieldLabel}>Promo Code</div>
            {editing ? (
              <div className={styles.editPromoCode}>
                <input
                  type="text"
                  className={styles.editInput}
                  value={newCode}
                  onChange={(e) => {
                    setNewCode(e.target.value.toUpperCase());
                    setEditError('');
                  }}
                  maxLength={20}
                  autoFocus
                />
                <div className={styles.editActions}>
                  <button type="button" className={styles.copyBtn} onClick={handleSaveCode} disabled={updating}>
                    {updating ? 'Saving...' : 'Save'}
                  </button>
                  <button type="button" className={styles.cancelBtn} onClick={handleCancelEdit}>
                    Cancel
                  </button>
                </div>
                {editError && <div className={styles.editError}>{editError}</div>}
                <div className={styles.editWarning}>
                  The new code will be active immediately. The old code will stop working.
                </div>
              </div>
            ) : (
              <div className={styles.offerFieldValue}>
                <span className={styles.mono}>{enrollment.promoCodeAlias}</span>
                <button
                  type="button"
                  className={styles.copyBtn}
                  onClick={() => copyToClipboard(enrollment.promoCodeAlias, 'Promo code')}
                >
                  Copy
                </button>
                <button type="button" className={styles.changeBtn} onClick={() => setEditing(true)}>
                  Change
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.blurbCard}>
        <div className={styles.blurbHeader}>
          <div className={styles.offerTitle}>Share with adopters</div>
          <div className={styles.blurbTabs}>
            <button
              type="button"
              className={`${styles.blurbTab} ${activeBlurbTab === 'email' ? styles.blurbTabActive : ''}`}
              onClick={() => setActiveBlurbTab('email')}
            >
              Email
            </button>
            <button
              type="button"
              className={`${styles.blurbTab} ${activeBlurbTab === 'print' ? styles.blurbTabActive : ''}`}
              onClick={() => setActiveBlurbTab('print')}
            >
              Print
            </button>
          </div>
        </div>
        <pre className={styles.blurbText}>{activeBlurbTab === 'email' ? emailBlurb : printBlurb}</pre>
        <button
          type="button"
          className={styles.copyBtn}
          onClick={() => copyToClipboard(activeBlurbTab === 'email' ? emailBlurb : printBlurb, 'Blurb')}
        >
          Copy to clipboard
        </button>
      </div>

      <div className={styles.activationsCard}>
        <div className={styles.offerTitle}>Adopter Activations</div>
        {enrollment.recentRedemptions.length === 0 ? (
          <p className={styles.emptyTable}>
            No activations yet. Share your link and promo code with adopters to get started.
          </p>
        ) : (
          <table className={styles.activationsTable}>
            <thead>
              <tr>
                <th>Pet Name</th>
                <th>Adopter</th>
                <th>Breed</th>
                <th>Age</th>
                <th>Activated</th>
              </tr>
            </thead>
            <tbody>
              {enrollment.recentRedemptions.map((r) => (
                <tr key={r.id}>
                  <td>{r.petName ?? '—'}</td>
                  <td>{r.adopterName}</td>
                  <td>{r.breed ?? '—'}</td>
                  <td>{r.age ?? '—'}</td>
                  <td>{DateTime.fromISO(r.redeemedAt).toLocaleString(DateTime.DATE_MED)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
